"use client";
import React from "react";
import * as z from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { MailIcon, TriangleAlertIcon } from "lucide-react";
import Image from "next/image";
import { ResetPasswordSchema } from "@/schemas/auth-schema";
import { resetPassword } from "@/actions/auth/lib/reset-password";
import { CardWrapper } from "@/app/auth/(lib)/components/card-wrapper";

export function ResetForm() {
  const [showEmailSent, setShowEmailSent] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string | undefined>("");

  const [isPending, startTransition] = React.useTransition();

  const form = useForm<z.infer<typeof ResetPasswordSchema>>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof ResetPasswordSchema>) => {
    setError("");

    startTransition(async () => {
      resetPassword(values)
        .then(async (data) => {
          if (data.error) {
            form.reset();
            setError(data.error);
          }
          if (data.emailSent) {
            setShowEmailSent(true);
          }
        })
        .catch(() => {
          setError("Something went wrong!");
        });
    });
  };

  return (
    <CardWrapper backButtonLabel="Back to login" backButtonHref="/auth/login">
      <div className="flex flex-col gap-2 w-full items-center justify-center p-4">
        <Image src={`/assets/logo_512x512.png`} alt="avatar" width={100} height={100} />
        <span className="text-primary font-bold text-2xl">Glovebox</span>
        {!showEmailSent && <span className="text-foreground/50 text-sm">Reset your password</span>}
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {!showEmailSent && (
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <Input
                      {...field}
                      disabled={isPending}
                      placeholder="john.doe@example.com"
                      type="email"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {error && (
            <div className="text-destructive flex items-center gap-x-2 rounded-md bg-destructive/15 p-3 text-sm">
              <TriangleAlertIcon className="h-4 w-4" />
              <p>{error}</p>
            </div>
          )}

          {showEmailSent && (
            <div className="flex-col items-center justify-center space-y-8 text-center mt-8">
              <div className="flex flex-col items-center justify-center space-y-2 text-center">
                <span className="font-bold text-2xl">Password reset email sent!</span>
                <div className="flex w-full items-center justify-center">
                  <MailIcon size="40" />
                </div>
                <span className="text-lg text-muted-foreground">Please check your inbox.</span>
              </div>
            </div>
          )}

          {!showEmailSent && (
            <Button type="submit" className="w-full mt-4" disabled={isPending}>
              Send password reset email
            </Button>
          )}
        </form>
      </Form>
    </CardWrapper>
  );
}
